import { Fragment } from "react";
import { Box, Text } from "@mantine/core";
import { defineMessages, useIntl } from "react-intl";
import type { PensionGroup, PensionSegment } from "./pensionSegments";
import classes from "./PensionDistributionBar.module.css";

const GROUP_ORDER: PensionGroup[] = ["private", "occupational", "public"];

// Static ids: a template-string id is invisible to the message extractor, so those
// labels could never be translated.
const GROUP_MESSAGES = defineMessages({
  private: {
    id: "pensionOverview.summaryCard.PrivatePension",
    defaultMessage: "Privat pension",
  },
  occupational: {
    id: "pensionOverview.summaryCard.occupationalPension",
    defaultMessage: "Tjänstepension",
  },
  public: {
    id: "pensionOverview.summaryCard.statePension",
    defaultMessage: "Allmän pension",
  },
});

const SEGMENT_MESSAGES = defineMessages({
  private: {
    id: "pensionOverview.summaryCard.PrivatePension",
    defaultMessage: "Privat pension",
  },
  occupationalDc: {
    id: "pensionOverview.summaryCard.nonDefinedBenefitPension",
    defaultMessage: "Premiebestämd",
  },
  occupationalNonDc: {
    id: "pensionOverview.summaryCard.definedBenefitPension",
    defaultMessage: "Förmånsbestämd",
  },
  ppm: {
    id: "pensionOverview.summaryCard.premiumPension",
    defaultMessage: "Premiepension",
  },
  income: {
    id: "pensionOverview.summaryCard.incomePension",
    defaultMessage: "Inkomstpension",
  },
  guarantee: {
    id: "pensionOverview.summaryCard.guaranteePension",
    defaultMessage: "Garantipension",
  },
});

export function PensionDistributionBar({
  segments,
}: {
  segments: PensionSegment[];
}) {
  const intl = useIntl();

  if (segments.length === 0) {
    return null;
  }

  const labelOf = (segment: PensionSegment) => {
    const message =
      SEGMENT_MESSAGES[segment.key as keyof typeof SEGMENT_MESSAGES];
    return message ? intl.formatMessage(message) : segment.label;
  };

  const ariaLabel = [
    intl.formatMessage({
      id: "app.poaOverview.distribution.ariaLabel",
      defaultMessage: "Fördelning av din pension",
    }),
    segments
      .map(
        (segment) =>
          `${labelOf(segment)} ${Math.round(segment.percentage)} procent`,
      )
      .join(", "),
  ].join(": ");

  const firstKey = segments[0]?.key;
  const lastKey = segments[segments.length - 1]?.key;

  return (
    <Box className={classes.bar} role="img" aria-label={ariaLabel}>
      {/* Labels and slices are siblings in one column, so the leftover height
          is shared over the slices alone, not re-split group by group. */}
      <Box className={classes.inner}>
        {GROUP_ORDER.map((group) => {
          const groupSegments = segments.filter(
            (segment) => segment.group === group,
          );
          if (groupSegments.length === 0) {
            return null;
          }
          return (
            <Fragment key={group}>
              <Text
                size="xs"
                c="var(--wallet-color-neutrals-gray-600)"
                className={classes.groupLabel}
              >
                {intl.formatMessage(GROUP_MESSAGES[group])}
              </Text>
              {groupSegments.map((segment) => (
                <Box
                  key={segment.key}
                  className={[
                    classes.block,
                    classes[segment.key],
                    segment.key === firstKey ? classes.radiusTop : "",
                    segment.key === lastKey ? classes.radiusBottom : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{ flexGrow: segment.percentage }}
                >
                  <Text
                    size="xs"
                    ta="center"
                    c="var(--wallet-color-neutrals-black)"
                  >
                    {labelOf(segment)} {Math.round(segment.percentage)} %
                  </Text>
                </Box>
              ))}
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
